import { comparatorContactPolicy, contactFieldsDisabled } from '@/utils/comparatorMinorContact'

/**
 * Single declarative source of truth for *which* fields a comparator must
 * fill out, given its context (testing vs not, sample-type block enabled,
 * MRN obligation, research-contact obligation, minor DOB).
 *
 * Historically the "is this field required?" decision was hand-coded inside
 * `ComparatorModal.computeErrors`, independent of the page-level completion
 * checks. Centralizing the required-field set here lets the modal and
 * `FormCompletionChecker` read one descriptor instead of re-encoding the
 * rules, so the two cannot silently diverge.
 *
 * This module owns *requiredness* only. Format validation (email/phone/
 * free-text allowlist) and UI disable/alert presentation stay at the call
 * site — they are presentation-bound, not "required field" rules.
 */

/** Fields whose presence a comparator's context can make mandatory. */
export type ComparatorRequiredField =
  | 'relationship'
  | 'firstName'
  | 'lastName'
  | 'sex'
  | 'dateOfBirth'
  | 'phenotypicTrait'
  | 'sampleType'
  | 'mrn'
  | 'contactChannel'

export interface ComparatorRequirementContext {
  /** Whether this comparator contributes a sample (drives sample-type / contact rules). */
  isTesting: boolean
  /** Sample-type block is rendered for this caller (F.1b-iv-c). */
  enableSampleType?: boolean
  /** Sample type is caller-supplied and not user-correctable, so it is not required here (F.1b-vii-a). */
  lockSampleType?: boolean
  /** MRN is mandatory for this caller (F.1b-vii-a). */
  mrnRequired?: boolean
  /**
   * A research-contact obligation applies to this comparator — either the
   * comparator's own opt-in or the proband-side opt-in projected onto this
   * row (F.1b-vii-b). When set (and testing), a contact channel is required.
   */
  researchContactRequired?: boolean
  /**
   * This comparator is the primary guardian AND the proband opted into research
   * contact, so it owns the proband's research-contact channel. Unlike
   * `researchContactRequired`, this obligates a contact channel regardless of
   * `isTesting` — a primary guardian who consents but isn't sequenced still
   * must supply email/phone for CareBus research follow-up.
   */
  primaryGuardianResearchContactRequired?: boolean
  /**
   * Clinical identity (date of birth + phenotypic trait) is mandatory for this
   * comparator. Mirrors the page-level completeness rule in
   * `FormCompletionChecker.isParentDataFullyComplete`, so a comparator the
   * modal accepts is also "complete" per the page (Phase 2c convergence).
   */
  requiresClinicalIdentity?: boolean
  /**
   * When `contactFieldsDisabled(comparatorContactPolicy(...))` is true
   * (`blockedMinor` or `disabledPendingDob`), `contactChannel` is omitted even
   * if a research-contact obligation would otherwise apply (GEN-5651).
   */
  dateOfBirth?: string | null
}

/** Fields every comparator must always provide, regardless of context. */
const ALWAYS_REQUIRED: readonly ComparatorRequiredField[] = [
  'relationship',
  'firstName',
  'lastName',
  'sex',
  'dateOfBirth',
]

/**
 * Resolve the set of fields the comparator must provide for `ctx`. Returns a
 * fresh `Set` so callers can mutate/inspect freely.
 */
export function requiredComparatorFields(ctx: ComparatorRequirementContext): Set<ComparatorRequiredField> {
  const required = new Set<ComparatorRequiredField>(ALWAYS_REQUIRED)
  if (ctx.requiresClinicalIdentity) {
    // `dateOfBirth` is always required (see ALWAYS_REQUIRED); clinical identity
    // additionally mandates the phenotypic trait.
    required.add('phenotypicTrait')
  }
  if (ctx.enableSampleType && !ctx.lockSampleType && ctx.isTesting) {
    required.add('sampleType')
  }
  if (ctx.mrnRequired) {
    required.add('mrn')
  }
  const contactObligated =
    (ctx.isTesting && ctx.researchContactRequired === true) || ctx.primaryGuardianResearchContactRequired === true
  // A primary guardian owns the proband's research-contact channel regardless
  // of whether they contribute a sample, so that obligation is not gated on
  // `isTesting` (unlike `researchContactRequired` alone).
  if (contactObligated && !contactFieldsDisabled(comparatorContactPolicy(ctx.dateOfBirth ?? ''))) {
    required.add('contactChannel')
  }
  return required
}
